/**
 * Highlight Decoration Plugin for ProseMirror
 *
 * This plugin renders visual highlight decorations on the document. It uses
 * ProseMirror's DecorationSet to overlay highlights without modifying the
 * document content.
 *
 * Features:
 * - Renders colored background highlights with underlines
 * - Handles overlapping highlights by creating non-overlapping segments
 * - Provides hover and click callbacks for tooltip interactions
 * - Supports hover state to visually emphasize hovered highlights
 * - Mobile/touch support with tap-to-select highlights
 */

import type { Transaction, EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  getPositionedPhrases,
  type PositionedPhrase,
} from "./positionedPhrasePlugin";

/**
 * Highlight data required for rendering.
 */
export interface HighlightRenderData {
  /** Unique identifier for this highlight */
  id: string;
  /** Color for the highlight (hex format, e.g., "#fbbf24") */
  color: string;
  /** Label shown in tooltip */
  label?: string;
  /** Any additional data to pass through */
  data?: unknown;
}

/**
 * Information about a hovered highlight.
 */
export interface HoveredHighlight {
  id: string;
  rect: DOMRect;
  data: PositionedPhrase;
  color: string;
  label: string;
}

interface PluginState {
  decorations: DecorationSet;
  hoveredHighlightIds: Set<string>;
}

export const highlightDecorationPluginKey = new PluginKey<PluginState>(
  "highlightDecorations"
);

/**
 * Set which highlights are currently hovered.
 */
export function setHoveredHighlights(
  state: EditorState,
  ids: Set<string>
): Transaction {
  return state.tr.setMeta(highlightDecorationPluginKey, { hoveredIds: ids });
}

/**
 * Get currently hovered highlight IDs.
 */
export function getHoveredHighlights(state: EditorState): Set<string> {
  return (
    highlightDecorationPluginKey.getState(state)?.hoveredHighlightIds ??
    new Set()
  );
}

export interface HighlightDecorationPluginOptions {
  /**
   * Called when user hovers over highlights.
   * Receives array of highlights at the hovered position.
   */
  onHover?: (highlights: HoveredHighlight[]) => void;

  /**
   * Called when user moves mouse away from highlights.
   */
  onLeave?: () => void;

  /**
   * Called when user clicks on a highlight.
   */
  onClick?: (highlight: HoveredHighlight) => void;

  /**
   * Function to get render data (color, label) for a positioned phrase.
   * This maps from the generic PositionedPhrase to highlight-specific data.
   */
  getRenderData: (phrase: PositionedPhrase) => HighlightRenderData;
}

/**
 * Creates the highlight decoration plugin.
 */
export function createHighlightDecorationPlugin(
  options: HighlightDecorationPluginOptions
): Plugin<PluginState> {
  const { getRenderData } = options;

  // Debounce timeout for hover events to reduce transaction spam
  let hoverDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

  // Track touch state to distinguish tap from scroll
  let touchStartTarget: HTMLElement | null = null;
  let touchStartTime = 0;
  let touchMoved = false;

  return new Plugin<PluginState>({
    key: highlightDecorationPluginKey,

    state: {
      init(_, state): PluginState {
        const phrases = getPositionedPhrases(state);
        return {
          decorations: createDecorations(
            state.doc,
            phrases,
            new Set(),
            getRenderData
          ),
          hoveredHighlightIds: new Set(),
        };
      },

      apply(tr, pluginState, oldState, newState): PluginState {
        // Check for hover state update via meta
        const meta = tr.getMeta(highlightDecorationPluginKey) as
          | { hoveredIds: Set<string> }
          | undefined;
        const newHoveredIds = meta?.hoveredIds ?? pluginState.hoveredHighlightIds;

        // Check if hover state changed
        const hoverChanged =
          meta?.hoveredIds !== undefined &&
          !setsEqual(newHoveredIds, pluginState.hoveredHighlightIds);

        // Check if positioned phrases changed
        const oldPhrases = getPositionedPhrases(oldState);
        const newPhrases = getPositionedPhrases(newState);
        const phrasesChanged = !positionedPhrasesEqual(oldPhrases, newPhrases);

        // Rebuild decorations if document changed, phrases changed, or hover state changed
        if (tr.docChanged || hoverChanged || phrasesChanged) {
          return {
            decorations: createDecorations(
              tr.doc,
              newPhrases,
              newHoveredIds,
              getRenderData
            ),
            hoveredHighlightIds: newHoveredIds,
          };
        }

        return pluginState;
      },
    },

    props: {
      decorations(state) {
        return highlightDecorationPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
      },

      handleDOMEvents: {
        mousemove(view: EditorView, event: MouseEvent) {
          // Debounce mousemove to ~60fps to reduce transaction spam
          if (hoverDebounceTimeout !== null) {
            clearTimeout(hoverDebounceTimeout);
          }
          hoverDebounceTimeout = setTimeout(() => {
            handleMouseMove(view, event, options);
            hoverDebounceTimeout = null;
          }, 16); // ~60fps frame boundary
          return false;
        },
        mouseenter(view: EditorView, event: MouseEvent) {
          // Immediate on mouseenter for responsiveness
          handleMouseMove(view, event, options);
          return false;
        },
        mouseleave(view: EditorView) {
          // Clear any pending debounced hover
          if (hoverDebounceTimeout !== null) {
            clearTimeout(hoverDebounceTimeout);
            hoverDebounceTimeout = null;
          }
          // Clear hover state
          const tr = view.state.tr.setMeta(highlightDecorationPluginKey, {
            hoveredIds: new Set<string>(),
          });
          view.dispatch(tr);
          options.onLeave?.();
          return false;
        },
        click(view: EditorView, event: MouseEvent) {
          handleClick(view, event, options);
          return false;
        },
        // Touch event support for mobile devices
        touchstart(_view: EditorView, event: TouchEvent) {
          // Record touch start for tap detection
          touchStartTarget = event.target as HTMLElement;
          touchStartTime = Date.now();
          touchMoved = false;
          return false;
        },
        touchmove() {
          // Mark as moved to prevent treating scroll as tap
          touchMoved = true;
          return false;
        },
        touchend(view: EditorView, event: TouchEvent) {
          // Only process as tap if:
          // 1. Touch didn't move (not a scroll)
          // 2. Touch duration was short (< 300ms, not a long press for text selection)
          const touchDuration = Date.now() - touchStartTime;
          const isTap = !touchMoved && touchDuration < 300;

          if (isTap && touchStartTarget) {
            // Find the highlight element from the touch target
            const highlightElement = touchStartTarget.classList.contains("highlight-decoration")
              ? touchStartTarget
              : touchStartTarget.closest(".highlight-decoration");

            if (highlightElement) {
              // Treat tap as click on highlight
              handleTouchTap(view, highlightElement as HTMLElement, options);
              // Prevent default to avoid double-handling
              event.preventDefault();
            }
          }

          // Reset touch state
          touchStartTarget = null;
          touchStartTime = 0;
          touchMoved = false;
          return false;
        },
      },
    },

    view() {
      return {
        destroy() {
          if (hoverDebounceTimeout !== null) {
            clearTimeout(hoverDebounceTimeout);
            hoverDebounceTimeout = null;
          }
        },
      };
    },
  });
}

/**
 * Create decoration set for all highlights.
 */
function createDecorations(
  doc: ProseMirrorNode,
  phrases: PositionedPhrase[],
  hoveredIds: Set<string>,
  getRenderData: (phrase: PositionedPhrase) => HighlightRenderData
): DecorationSet {
  if (phrases.length === 0) {
    return DecorationSet.empty;
  }

  // Create non-overlapping segments
  const segments = createHighlightSegments(phrases);
  const decorations: Decoration[] = [];
  const maxPos = doc.content.size;

  segments.forEach((segment) => {
    // Validate segment bounds
    if (segment.from < 0 || segment.to > maxPos || segment.from >= segment.to) {
      return;
    }

    // Determine which phrase to display for this segment
    // Prefer hovered phrase, otherwise use first
    let displayPhrase = segment.phrases[0];
    for (const phrase of segment.phrases) {
      if (hoveredIds.has(phrase.id)) {
        displayPhrase = phrase;
        break;
      }
    }

    const renderData = getRenderData(displayPhrase);
    const isHovered = hoveredIds.has(displayPhrase.id);
    const backgroundColor = isHovered
      ? `${renderData.color}66` // 40% opacity
      : `${renderData.color}33`; // 20% opacity

    // Create data attributes for all phrases in this segment
    const testIds = segment.phrases
      .map((p) => `highlight-${p.id}`)
      .join(" ");

    const decoration = Decoration.inline(
      segment.from,
      segment.to,
      {
        class: "highlight-decoration",
        style: `background-color: ${backgroundColor}; border-bottom: 2px solid ${renderData.color}; transition: background-color 0.15s ease; cursor: pointer;`,
        "data-highlight-id": displayPhrase.id,
        "data-testid": testIds,
        "data-color": renderData.color,
      }
    );

    decorations.push(decoration);
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * Segment of text covered by one or more highlights.
 */
interface HighlightSegment {
  from: number;
  to: number;
  phrases: PositionedPhrase[];
}

/**
 * Event type for sweep-line algorithm.
 */
type SweepEvent = {
  pos: number;
  type: "start" | "end";
  phrase: PositionedPhrase;
};

/**
 * Create non-overlapping segments from potentially overlapping phrases.
 * Uses sweep-line algorithm for O(n log n) performance instead of O(n²).
 */
function createHighlightSegments(
  phrases: PositionedPhrase[]
): HighlightSegment[] {
  if (phrases.length === 0) return [];

  // Build sorted event list: start and end events for each phrase
  const events: SweepEvent[] = [];
  for (const phrase of phrases) {
    events.push({ pos: phrase.startPos, type: "start", phrase });
    events.push({ pos: phrase.endPos, type: "end", phrase });
  }

  // Sort events by position, with "end" events before "start" events at same position
  // This ensures we close segments before opening new ones at the same boundary
  events.sort((a, b) => {
    if (a.pos !== b.pos) return a.pos - b.pos;
    // End events before start events at same position
    return a.type === "end" ? -1 : 1;
  });

  const segments: HighlightSegment[] = [];
  const activePhrases = new Map<string, PositionedPhrase>();
  let currentPos = events[0].pos;

  // Sweep through events, maintaining active phrase set
  for (const event of events) {
    // If position changed and we have active phrases, create a segment
    if (event.pos > currentPos && activePhrases.size > 0) {
      segments.push({
        from: currentPos,
        to: event.pos,
        phrases: Array.from(activePhrases.values()),
      });
    }

    // Update active set based on event type
    if (event.type === "start") {
      activePhrases.set(event.phrase.id, event.phrase);
    } else {
      activePhrases.delete(event.phrase.id);
    }

    currentPos = event.pos;
  }

  return segments;
}

/**
 * Handle mouse move over editor.
 */
function handleMouseMove(
  view: EditorView,
  event: MouseEvent,
  options: HighlightDecorationPluginOptions
) {
  const { onHover, onLeave, getRenderData } = options;
  const target = event.target as HTMLElement;

  // Find highlight element
  const highlightElement = target.classList.contains("highlight-decoration")
    ? target
    : (target.closest(".highlight-decoration"));

  if (!highlightElement) {
    // Not hovering over highlight
    const currentHovered = getHoveredHighlights(view.state);
    if (currentHovered.size > 0) {
      const tr = view.state.tr.setMeta(highlightDecorationPluginKey, {
        hoveredIds: new Set<string>(),
      });
      view.dispatch(tr);
      onLeave?.();
    }
    return;
  }

  const highlightId = highlightElement.getAttribute("data-highlight-id");
  if (!highlightId) return;

  // Update hover state
  const currentHovered = getHoveredHighlights(view.state);
  if (!currentHovered.has(highlightId)) {
    const tr = view.state.tr.setMeta(highlightDecorationPluginKey, {
      hoveredIds: new Set([highlightId]),
    });
    view.dispatch(tr);
  }

  // Find and call hover callback
  if (onHover) {
    const phrases = getPositionedPhrases(view.state);
    const hoveredPhrases = phrases.filter((p) => p.id === highlightId);

    const highlights: HoveredHighlight[] = hoveredPhrases.map((phrase) => {
      const renderData = getRenderData(phrase);
      const rect = getBoundingRectForPhrase(view, highlightId);
      return {
        id: phrase.id,
        rect,
        data: phrase,
        color: renderData.color,
        label: renderData.label ?? phrase.id,
      };
    });

    if (highlights.length > 0) {
      onHover(highlights);
    }
  }
}

/**
 * Handle click on editor.
 */
function handleClick(
  view: EditorView,
  event: MouseEvent,
  options: HighlightDecorationPluginOptions
) {
  const { onClick, getRenderData } = options;
  if (!onClick) return;

  const target = event.target as HTMLElement;
  const highlightElement = target.classList.contains("highlight-decoration")
    ? target
    : (target.closest(".highlight-decoration"));

  if (!highlightElement) return;

  const highlightId = highlightElement.getAttribute("data-highlight-id");
  if (!highlightId) return;

  const phrases = getPositionedPhrases(view.state);
  const phrase = phrases.find((p) => p.id === highlightId);

  if (phrase) {
    const renderData = getRenderData(phrase);
    const rect = getBoundingRectForPhrase(view, highlightId);
    onClick({
      id: phrase.id,
      rect,
      data: phrase,
      color: renderData.color,
      label: renderData.label ?? phrase.id,
    });
  }
}

/**
 * Handle touch tap on a highlight element.
 * This is called from touchend when we detect a tap gesture.
 */
function handleTouchTap(
  view: EditorView,
  highlightElement: HTMLElement,
  options: HighlightDecorationPluginOptions
) {
  const { onClick, onHover, getRenderData } = options;

  const highlightId = highlightElement.getAttribute("data-highlight-id");
  if (!highlightId) return;

  const phrases = getPositionedPhrases(view.state);
  const phrase = phrases.find((p) => p.id === highlightId);

  if (!phrase) return;

  const renderData = getRenderData(phrase);
  const rect = getBoundingRectForPhrase(view, highlightId);

  // Update hover state to show the highlight as selected
  const tr = view.state.tr.setMeta(highlightDecorationPluginKey, {
    hoveredIds: new Set([highlightId]),
  });
  view.dispatch(tr);

  // Call onHover first to show the tooltip
  if (onHover) {
    onHover([{
      id: phrase.id,
      rect,
      data: phrase,
      color: renderData.color,
      label: renderData.label ?? phrase.id,
    }]);
  }

  // Then call onClick if provided
  if (onClick) {
    onClick({
      id: phrase.id,
      rect,
      data: phrase,
      color: renderData.color,
      label: renderData.label ?? phrase.id,
    });
  }
}

/**
 * Get combined bounding rect for all elements with a highlight ID.
 */
function getBoundingRectForPhrase(view: EditorView, highlightId: string): DOMRect {
  const elements = view.dom.querySelectorAll(
    `[data-highlight-id="${highlightId}"]`
  );

  if (elements.length === 0) {
    return new DOMRect(0, 0, 0, 0);
  }

  let minTop = Infinity;
  let minLeft = Infinity;
  let maxBottom = -Infinity;
  let maxRight = -Infinity;

  elements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    minTop = Math.min(minTop, rect.top);
    minLeft = Math.min(minLeft, rect.left);
    maxBottom = Math.max(maxBottom, rect.bottom);
    maxRight = Math.max(maxRight, rect.right);
  });

  return new DOMRect(
    minLeft,
    minTop,
    maxRight - minLeft,
    maxBottom - minTop
  );
}

/**
 * Check if two sets are equal.
 */
function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/**
 * Check if two arrays of positioned phrases are equal.
 * Compares by id, startPos, and endPos.
 */
function positionedPhrasesEqual(
  a: PositionedPhrase[],
  b: PositionedPhrase[]
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].id !== b[i].id ||
      a[i].startPos !== b[i].startPos ||
      a[i].endPos !== b[i].endPos
    ) {
      return false;
    }
  }
  return true;
}
